import React, { useEffect, useState } from "react";
import { ListOrdered, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/PageHeader";
import api from "@/lib/api";
import SceneTrendRow from "@/components/shownotes/SceneTrendRow";
import SceneListDialog from "@/components/shownotes/SceneListDialog";

// Directors + stage managers: per-scene average minutes and whether each scene is tightening up.
export default function SceneTrendsTab({ productions, director }) {
  const [pid, setPid] = useState(null);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    setData(null);
    api.get("/show-notes/scene-trends", { params: pid ? { production_id: pid } : {} }).then(({ data }) => setData(data)).catch(() => setData({ scenes: [] }));
  }, [pid, reload]);
  const shown = data?.production?.id || pid;
  const production = productions.find((p) => p.id === shown) || data?.production;
  const scenes = data?.scenes || [];
  const faster = scenes.filter((s) => s.trend === "faster").length;
  const slower = scenes.filter((s) => s.trend === "slower").length;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={shown || ""} onValueChange={setPid} disabled={!productions.length}>
          <SelectTrigger className="w-64 bg-white" data-testid="scene-trends-production-select"><SelectValue placeholder="Choose a production" /></SelectTrigger>
          <SelectContent>{productions.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`scene-trends-production-${p.id}`}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
        {scenes.length > 0 && (
          <span className="text-sm text-slate-500" data-testid="scene-trends-summary">
            Across {data.rehearsals} rehearsal{data.rehearsals === 1 ? "" : "s"} · <span className="font-semibold text-emerald-700">{faster} tightening up</span>
            {slower > 0 && <> · <span className="font-semibold text-rose-700">{slower} running longer</span></>}
          </span>
        )}
        {director && (
          <Button variant="outline" className="ml-auto rounded-full bg-white" disabled={!production} onClick={() => setEditing(true)} data-testid="scene-trends-edit-list-btn">
            <ListOrdered className="mr-1.5 h-4 w-4" /> Edit scene list
          </Button>
        )}
      </div>
      {director && <SceneListDialog production={production} open={editing} onOpenChange={setEditing} onSaved={() => setReload((r) => r + 1)} />}
      {!data ? (
        <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !scenes.length ? (
        <EmptyState title="No scene run times yet" description="Scene trends appear once Rehearsal Notes with Scene Run times are published for this production." testId="scene-trends-empty" />
      ) : (
        <Card className="border-slate-200 px-4 py-1 shadow-sm animate-fade-in-up" data-testid="scene-trends-list">
          <div className="hidden grid-cols-[1fr_8rem_6rem_10rem] gap-3 border-b border-slate-100 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-400 sm:grid">
            <span>Scene</span><span>Each run</span><span className="text-right">Average</span><span className="text-right">First vs last runs</span>
          </div>
          {scenes.map((s, i) => <SceneTrendRow key={s.name} s={s} i={i} />)}
        </Card>
      )}
    </div>
  );
}
